import { useMemo, useState, type ReactNode } from 'react';
import type { ApprovalRequest } from '@/lib/types';
import { formatPKR } from '../format';
import { BucketShell } from './_BucketShell';
import { readDefaultFields, readStructuredFields } from './_payload';

export interface GrnNotFoundViewProps {
  req: ApprovalRequest;
}

function readNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function readText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function readAmount(value: unknown): string {
  return formatPKR(readNumber(value));
}

function candidateGrnNo(candidate: Record<string, unknown>): string {
  return readText(candidate['grn_no'] ?? candidate['GRN_NO']);
}

function readCandidates(sf: Record<string, unknown>): Record<string, unknown>[] {
  const candidates =
    Array.isArray(sf['fallback_grn_candidates']) && sf['fallback_grn_candidates'].length > 0
      ? sf['fallback_grn_candidates']
      : sf['postable_grn_candidates'];
  if (!Array.isArray(candidates)) return [];
  return candidates.filter((item): item is Record<string, unknown> => isRecord(item));
}

function isFalse(value: unknown): boolean {
  return value === false || String(value).toLowerCase() === 'false';
}

function isTrue(value: unknown): boolean {
  return value === true || String(value).toLowerCase() === 'true';
}

function isBlockedStatus(candidate: Record<string, unknown>): boolean {
  const authStatusId = String(
    candidate['auth_status_id'] ?? candidate['AUTH_STATUS_ID'] ?? '',
  ).trim();
  if (['5', '10', '15', '50'].includes(authStatusId)) return true;
  const status = String(
    candidate['auth_status'] ??
      candidate['AUTH_STATUS'] ??
      candidate['status'] ??
      candidate['STATUS'] ??
      '',
  )
    .trim()
    .toLowerCase();
  return /unapproved|not approved|pending|sent for approval|awaiting/.test(status);
}

function hasUsefulAmount(candidate: Record<string, unknown>): boolean {
  return [
    'grn_net_amount',
    'GRN_NET_AMOUNT',
    'grn_tax_amount',
    'GRN_TAX_AMOUNT',
    'grn_gross_amount',
    'GRN_GROSS_AMOUNT',
    'grn_amount',
    'GRN_AMOUNT',
    'amount',
    'AMOUNT',
    'remaining_amount',
    'REMAINING_AMOUNT',
  ].some((key) => readNumber(candidate[key]) !== null);
}

function candidateRank(candidate: Record<string, unknown>): number {
  let score = 0;
  if (!isFalse(candidate['supplier_context_mismatch'])) score += 30;
  if (!isFalse(candidate['already_invoiced'])) score += 30;
  if (isTrue(candidate['already_invoiced'])) score += 30;
  if (isBlockedStatus(candidate)) score += 20;
  if (!hasUsefulAmount(candidate)) score += 10;
  return score;
}

function rankedPrimaryCandidates(
  candidates: Record<string, unknown>[],
): Record<string, unknown>[] {
  const byGrn = new Map<string, Record<string, unknown>>();
  for (const candidate of candidates) {
    const grnNo = candidateGrnNo(candidate);
    const existing = byGrn.get(grnNo);
    if (!existing || candidateRank(candidate) < candidateRank(existing)) {
      byGrn.set(grnNo, candidate);
    }
  }
  return [...byGrn.values()].sort((a, b) => candidateRank(a) - candidateRank(b));
}

export function GrnNotFoundView({ req }: GrnNotFoundViewProps) {
  const fields = readDefaultFields(req);
  const sf = readStructuredFields(req);
  const candidates = readCandidates(sf);
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const rankedCandidates = useMemo(() => rankedPrimaryCandidates(candidates), [candidates]);
  const allCandidates = useMemo(
    () => [...candidates].sort((a, b) => candidateRank(a) - candidateRank(b)),
    [candidates],
  );
  const primaryCandidates = rankedCandidates.slice(0, 1);

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-orange-300 bg-orange-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
          {candidates.length > 0 ? 'GRN candidate found — amount needs review' : 'No GRN found'}
        </p>
        {candidates.length > 0 ? (
          <>
            <p className="mt-4 text-sm text-neutral-700">
              Oracle found likely GRN evidence for{' '}
              <span className="font-medium text-neutral-900">{fields.vendor ?? '—'}</span>,
              but the invoice amount still needs Finance review.
            </p>
            <p className="mt-12 text-sm text-neutral-600">
              Select the matching GRN in the reprocess form below, confirm the amounts,
              then save to revalidate. This row will not post until Finance confirms it.
            </p>
            <div className="mt-12 grid gap-8">
              {primaryCandidates.map((candidate, index) => (
                <CandidateSummary
                  key={`${candidateGrnNo(candidate)}-${readText(candidate['grn_det_id'])}-${index}`}
                  candidate={candidate}
                />
              ))}
            </div>
            {allCandidates.length > primaryCandidates.length && (
              <div className="mt-12">
                <button
                  type="button"
                  className="rounded-md border border-orange-300 bg-neutral-0 px-10 py-6 text-sm font-medium text-orange-900 hover:bg-orange-100"
                  onClick={() => setShowAllCandidates((open) => !open)}
                  aria-expanded={showAllCandidates}
                >
                  {showAllCandidates ? 'Hide all candidates' : 'Show all candidates'}
                </button>
                {showAllCandidates && (
                  <div className="mt-8">
                    <CandidateTable candidates={allCandidates} />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="mt-4 text-sm text-neutral-700">
              Oracle returned no matching GRN for{' '}
              <span className="font-medium text-neutral-900">{fields.vendor ?? '—'}</span>.
            </p>
            <p className="mt-12 text-sm text-neutral-600">
              Enter the correct GRN number(s) in the form below and save to revalidate, or
              use the &ldquo;Prepare service invoice&rdquo; action in the footer if this is
              a service charge with no GRN.
            </p>
          </>
        )}
      </section>
    </BucketShell>
  );
}

function CandidateSummary({ candidate }: { candidate: Record<string, unknown> }) {
  return (
    <div className="rounded-md border border-orange-200 bg-neutral-0 p-12">
      <div className="flex flex-wrap items-center justify-between gap-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-orange-800">
            Primary GRN candidate
          </p>
          <p className="mt-2 font-mono text-xl font-semibold text-neutral-900">
            {candidateGrnNo(candidate)}
          </p>
        </div>
        <div className="text-right text-sm text-neutral-600">
          <p>
            PO <span className="font-mono text-neutral-900">{readText(candidate['po_no'])}</span>
          </p>
          <p>
            Detail{' '}
            <span className="font-mono text-neutral-900">
              {readText(candidate['grn_det_id'] ?? candidate['grn_detail_id'])}
            </span>
          </p>
        </div>
      </div>
      <div className="mt-12 grid gap-8 text-sm tablet:grid-cols-3">
        <AmountMetric
          label="Net"
          value={
            candidate['grn_net_amount'] ??
            candidate['amount'] ??
            candidate['net_amount'] ??
            candidate['net_delta']
          }
        />
        <AmountMetric
          label="Tax"
          value={candidate['grn_tax_amount'] ?? candidate['tax_delta']}
        />
        <AmountMetric
          label="Gross"
          value={candidate['grn_gross_amount'] ?? candidate['gross_delta']}
        />
      </div>
    </div>
  );
}

function AmountMetric({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {label}
      </p>
      <p className="mt-2 font-mono font-semibold text-neutral-900">{readAmount(value)}</p>
    </div>
  );
}

function CandidateTable({ candidates }: { candidates: Record<string, unknown>[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-orange-200 bg-neutral-0">
      <table className="min-w-full divide-y divide-orange-100 text-sm">
        <thead className="bg-orange-100/60 text-xs font-semibold uppercase tracking-wide text-orange-900">
          <tr>
            <Th>GRN</Th>
            <Th>Detail</Th>
            <Th>PO</Th>
            <Th>Item</Th>
            <Th>Qty</Th>
            <Th>Rate</Th>
            <Th>Net amount</Th>
            <Th>Tax amount</Th>
            <Th>Gross amount</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-orange-100">
          {candidates.map((candidate, index) => (
            <tr key={`${readText(candidate['grn_det_id'])}-${index}`}>
              <Td mono>{candidateGrnNo(candidate)}</Td>
              <Td mono>{readText(candidate['grn_det_id'] ?? candidate['grn_detail_id'])}</Td>
              <Td mono>{readText(candidate['po_no'])}</Td>
              <Td>
                <div className="max-w-[260px]">
                  <p className="font-mono text-xs font-semibold text-neutral-900">
                    {readText(candidate['item_code'])}
                  </p>
                  <p className="mt-2 whitespace-normal text-xs text-neutral-600">
                    {readText(candidate['item_desc'] ?? candidate['detail_item_desc'])}
                  </p>
                </div>
              </Td>
              <Td mono>{readText(candidate['quantity'])}</Td>
              <Td mono>{readText(candidate['rate'] ?? candidate['po_rate'])}</Td>
              <Td mono>
                {readAmount(
                  candidate['grn_net_amount'] ??
                    candidate['amount'] ??
                    candidate['net_amount'] ??
                    candidate['net_delta'],
                )}
              </Td>
              <Td mono>
                {readAmount(candidate['grn_tax_amount'] ?? candidate['tax_delta'])}
              </Td>
              <Td mono>
                {readAmount(candidate['grn_gross_amount'] ?? candidate['gross_delta'])}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-12 py-8 text-left">{children}</th>;
}

function Td({ children, mono = false }: { children: ReactNode; mono?: boolean }) {
  return (
    <td
      className={`px-12 py-10 align-top text-neutral-800 ${
        mono ? 'font-mono text-xs font-semibold' : ''
      }`}
    >
      {children}
    </td>
  );
}
